/**
 * Панель настроек камеры для админ-панели
 * Enterprise Design v2.0
 */
import { useState, useRef, useEffect, useMemo } from 'react'
import { useCameraStore } from '../store/cameraStore'

export function CameraSettingsPanel() {
  const { settings, updateSettings, resetSettings } = useCameraStore()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [isPreviewActive, setIsPreviewActive] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // CSS фильтры для video элемента
  const videoFilters = useMemo(() => {
    const filters: string[] = []
    
    const brightnessPercent = (settings.brightness / 128) * 100
    const exposureBrightness = 100 + (settings.exposureCompensation * 15)
    const totalBrightness = (brightnessPercent / 100) * (exposureBrightness / 100) * 100
    filters.push(`brightness(${totalBrightness}%)`)
    
    const contrastPercent = (settings.contrast / 128) * 100
    filters.push(`contrast(${contrastPercent}%)`)
    
    filters.push(`saturate(${settings.saturation}%)`)
    
    if (settings.grayscale) {
      filters.push('grayscale(100%)')
    }
    
    if (settings.invert) {
      filters.push('invert(100%)')
    }
    
    return filters.join(' ')
  }, [
    settings.brightness,
    settings.contrast,
    settings.exposureCompensation,
    settings.saturation,
    settings.grayscale,
    settings.invert
  ])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.style.filter = videoFilters
    }
  }, [videoFilters])

  const startPreview = async () => {
    if (!videoRef.current) return

    try {
      setPreviewError(null)
      
      const constraints = {
        video: {
          facingMode: settings.preferredCamera === 'environment' ? 'environment' : 'user',
          width: { ideal: settings.videoResolution === 'low' ? 640 : settings.videoResolution === 'medium' ? 1280 : 1920 },
          height: { ideal: settings.videoResolution === 'low' ? 480 : settings.videoResolution === 'medium' ? 720 : 1080 },
        },
        audio: false
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.style.filter = videoFilters
        
        await new Promise((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => resolve(null)
          }
        })
        
        await videoRef.current.play()
      }
      
      setIsPreviewActive(true)
    } catch (error) {
      console.error('[CameraSettings] Failed to start camera preview:', error)
      setPreviewError('Не удалось запустить камеру: ' + (error instanceof Error ? error.message : String(error)))
      setIsPreviewActive(false)
    }
  }

  const stopPreview = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setIsPreviewActive(false)
  }

  useEffect(() => {
    return () => {
      stopPreview()
    }
  }, [])

  return (
    <div className="space-y-cards">
      {/* Preview Section */}
      <div className="focus-block overflow-hidden !p-0">
        <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
          <video
            ref={videoRef}
            className={`w-full h-full object-cover ${isPreviewActive ? 'block' : 'hidden'}`}
            playsInline
            muted
            autoPlay
          />
          
          {!isPreviewActive && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
              <button 
                onClick={startPreview} 
                className="btn btn-lg btn-primary"
              >
                <span>▶️</span>
                <span>Запустить предпросмотр</span>
              </button>
              {previewError && (
                <p className="mt-4 text-[14px] text-[#ef4444] text-center">{previewError}</p>
              )}
            </div>
          )}
          
          {isPreviewActive && (
            <button 
              onClick={stopPreview} 
              className="absolute top-4 right-4 btn btn-sm btn-secondary"
            >
              <span>⏸️</span>
              <span>Остановить</span>
            </button>
          )}
        </div>
      </div>

      {/* Camera Selection */}
      <div className="focus-block">
        <label className="block text-[13px] font-semibold text-[#6b6b6b] uppercase tracking-wide mb-2">
          📸 Камера
        </label>
        <select
          value={settings.preferredCamera}
          onChange={(e) =>
            updateSettings({ preferredCamera: e.target.value as 'user' | 'environment' })
          }
          className="input"
        >
          <option value="user">Фронтальная</option>
          <option value="environment">Задняя</option>
        </select>
      </div>

      {/* Brightness */}
      <div className="focus-block">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-white">
            🌟 Яркость
          </label>
          <span className="text-[14px] font-bold text-[#3b82f6] tabular-nums">
            {Math.round((settings.brightness / 128) * 100)}%
          </span>
        </div>
        <input
          type="range"
          min="32"
          max="255"
          step="1"
          value={settings.brightness}
          onChange={(e) => updateSettings({ brightness: parseInt(e.target.value) })}
          className="slider-oneui"
        />
        <p className="text-[11px] text-[#6b6b6b] mt-2 leading-tight">
          100% = норма, меньше = темнее (против засветов)
        </p>
      </div>

      {/* Contrast */}
      <div className="focus-block">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-white">
            ◐ Контрастность
          </label>
          <span className="text-[14px] font-bold text-[#3b82f6] tabular-nums">
            {Math.round((settings.contrast / 128) * 100)}%
          </span>
        </div>
        <input
          type="range"
          min="32"
          max="255"
          step="1"
          value={settings.contrast}
          onChange={(e) => updateSettings({ contrast: parseInt(e.target.value) })}
          className="slider-oneui"
        />
        <p className="text-[11px] text-[#6b6b6b] mt-2 leading-tight">
          100% = норма, больше = резче QR код
        </p>
      </div>

      {/* Exposure */}
      <div className="focus-block">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-white">
            ☀️ Экспозиция
          </label>
          <span className="text-[14px] font-bold text-[#3b82f6] tabular-nums">
            {settings.exposureCompensation > 0 ? '+' : ''}{settings.exposureCompensation}
          </span>
        </div>
        <input
          type="range"
          min="-3"
          max="3"
          step="0.5"
          value={settings.exposureCompensation}
          onChange={(e) => updateSettings({ exposureCompensation: parseFloat(e.target.value) })}
          className="slider-oneui"
        />
        <p className="text-[11px] text-[#6b6b6b] mt-2 leading-tight">
          Отрицательные = темнее (против засветов)
        </p>
      </div>

      {/* Saturation */}
      <div className="focus-block">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-white">
            🎨 Насыщенность
          </label>
          <span className="text-[14px] font-bold text-[#3b82f6] tabular-nums">
            {settings.saturation}%
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="200"
          step="5"
          value={settings.saturation}
          onChange={(e) => updateSettings({ saturation: parseInt(e.target.value) })}
          className="slider-oneui"
        />
        <p className="text-[11px] text-[#6b6b6b] mt-2 leading-tight">
          100% = норма, меньше = меньше засветов
        </p>
      </div>

      {/* Motion Sensitivity */}
      <div className="focus-block">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-white">
            👋 Чувствительность движения
          </label>
          <span className="text-[14px] font-bold text-[#3b82f6] tabular-nums">
            {settings.motionSensitivity}
          </span>
        </div>
        <input
          type="range"
          min="10"
          max="150"
          step="5"
          value={settings.motionSensitivity}
          onChange={(e) => updateSettings({ motionSensitivity: parseInt(e.target.value) })}
          className="slider-oneui"
        />
        <p className="text-[11px] text-[#6b6b6b] mt-2 leading-tight">
          Ниже = более чувствительно (камера активируется при малейшем движении), выше = менее чувствительно
        </p>
      </div>

      {/* Toggles */}
      <div className="focus-block !p-0 overflow-hidden">
        <label className="flex items-center justify-between cursor-pointer active:bg-[#242424] transition-colors border-b border-[#2e2e2e]" style={{padding: '20px 24px', minHeight: '72px'}}>
          <span className="text-[15px] font-medium text-white">
            ⚫ Черно-белый режим
          </span>
          <input
            type="checkbox"
            checked={settings.grayscale}
            onChange={(e) => updateSettings({ grayscale: e.target.checked })}
            className="toggle-switch"
          />
        </label>

        <label className="flex items-center justify-between cursor-pointer active:bg-[#242424] transition-colors" style={{padding: '20px 24px', minHeight: '72px'}}>
          <span className="text-[15px] font-medium text-white">
            🔄 Инверсия цветов
          </span>
          <input
            type="checkbox"
            checked={settings.invert}
            onChange={(e) => updateSettings({ invert: e.target.checked })}
            className="toggle-switch"
          />
        </label>
      </div>

      {/* Performance */}
      <div className="focus-block">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-white">
            ⚡ Частота сканирования
          </label>
          <span className="text-[14px] font-bold text-[#3b82f6] tabular-nums">
            {settings.maxScansPerSecond} FPS
          </span>
        </div>
        <input
          type="range"
          min="2"
          max="10"
          step="1"
          value={settings.maxScansPerSecond}
          onChange={(e) => updateSettings({ maxScansPerSecond: parseInt(e.target.value) })}
          className="slider-oneui"
        />
        <p className="text-[11px] text-[#6b6b6b] mt-2 leading-tight">
          Меньше = лучше для старых устройств
        </p>
      </div>

      {/* Video Resolution */}
      <div className="focus-block">
        <label className="block text-[13px] font-semibold text-[#6b6b6b] uppercase tracking-wide mb-2">
          🖼️ Разрешение видео
        </label>
        <select
          value={settings.videoResolution}
          onChange={(e) =>
            updateSettings({ videoResolution: e.target.value as 'low' | 'medium' | 'high' })
          }
          className="input"
        >
          <option value="low">Низкое (640×480) - быстрое</option>
          <option value="medium">Среднее (1280×720)</option>
          <option value="high">Высокое (1920×1080)</option>
        </select>
      </div>

      {/* Reset Button */}
      <button
        onClick={resetSettings}
        className="btn btn-lg btn-warning btn-full"
      >
        <span>🔄</span>
        <span>Сбросить настройки</span>
      </button>

      {/* Hint */}
      <div className="info-card">
        <div className="info-card-icon">💡</div>
        <div className="flex-1">
          <p className="text-white font-semibold text-[14px] mb-2">При засветах от экрана телефона:</p>
          <ul className="space-y-1 text-[12px] text-[#a0a0a0]">
            <li><strong className="text-white">Яркость:</strong> 50-80% (уменьшить)</li>
            <li><strong className="text-white">Контрастность:</strong> 120-150% (увеличить)</li>
            <li><strong className="text-white">Экспозиция:</strong> -1 или -2</li>
            <li><strong className="text-white">Насыщенность:</strong> 0-50% (убирает цветные засветы)</li>
            <li><strong className="text-white">Черно-белый:</strong> включить</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
